/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { useState, useEffect, useRef, useMemo } from "react";
import axios from "../api/axiosInstance"; // con token JWT incluido automáticamente

import { useSearchParams, useNavigate, useLocation, useParams } from "react-router-dom";

import { formatDate } from "../utils/dateHelpers";
import { getGuestContext, isEditingAnyRole } from "../utils/guestHelpers";
import { useCustomerForm } from "../hooks/useCustomerForm";
import { useReservationInitializer } from "../hooks/useReservationInitializer";
import { useSyncReservationId } from "../hooks/useSyncReservationId";
import { useReservationGroup } from "../hooks/useReservationGroup";
import GuestSelectorPopup from "../Components/GuestSelectorPopup";
import MainGuestSelectorPopup from "../Components/MainGuestSelectorPopup";
import CustomerSelectorPopupAccompanying from "../Components/CustomerSelectorPopupAccompanying";
import CustomerSelectorPopupChild from "../Components/CustomerSelectorPopupChild";




import {
  reloadDatesFromDB,
  reloadAdultsFromDB,
  reloadChildrenFromDB,
  reloadRoomCategoryIdFromDB,
  reloadRoomCategoryDetailsFromDB,
  reloadRoomIdFromDB,
  reloadRoomRateIdFromDB,
  reloadGroupRateIdFromDB,
} from "../utils/reloaders";
import { useLoadReservationData } from "../hooks/useLoadReservationData";
import { useCopyReservationInfo } from "../hooks/useCopyReservationInfo";
import { useValidateReservationCustomers } from "../hooks/useValidateReservationCustomers";
import { useReservationHandlers } from "../hooks/useReservationHandlers";
import { refetchReservationAfterReset } from "../utils/refetchers";
import { useGuestManagementHandlers } from "../hooks/useGuestManagementHandlers";
import GroupInfoCard from "../Components/GroupInfoCard";
import ReservationTabsPanel from "../Components/ReservationTabsPanel";
import ReservationForm from "../Components/ReservationForm";
import GuestsColumn from "../Components/GuestsColumn";
import GroupSummary from "../Components/GroupSummary";





const NewReservation = () => {

  const debugSetFormData = (origin) => (updater) => {
  setFormData(prev => {
    const next = typeof updater === "function" ? updater(prev) : updater;

    console.log("[FORMDATA][SET]", {
      origin,
      before: {
        room_category_id: prev.room_category_id,
        room_id: prev.room_id,
        rate_id: prev.rate_id,
      },
      after: {
        room_category_id: next.room_category_id,
        room_id: next.room_id,
        rate_id: next.rate_id,
      }
    });

    return next;
  });
};

  console.log("🟥 NewReservation: render inicial del componente");
  // Extract customer_id from URL

  useEffect(() => {
    console.log("🔥 NewReservation MOUNT");
    return () => console.log("💀 NewReservation UNMOUNT");
  }, []);


  // 🧑‍💼 Datos de usuario y permisos
  const currentUser = JSON.parse(localStorage.getItem("palantir_user"));
  const permissions = currentUser?.permissions || {};

 // 🌐 Navegación y parámetros de URL
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "1"; // Usa "1" como valor predeterminado si no se envía
  const navigate = useNavigate();
  const [isRecoveringReservation, setIsRecoveringReservation] = useState(false);
  const [formData, setFormData] = useState({
    check_in_date: "",
    check_in_time: "",
    check_out_date: "",
    check_out_time: "",
    arrival_date: "",
    arrival_time: "",
    departure_date: "",
    departure_time: "",
    room_category_id: "",
    room_id: "",
    rate_id: "",
    group_rate_id: null,
    room_nights: "",
    adults: "",
    children: "",
    children_ages: "",
    babies: 0,
    special_requests: "",
    payment_status: "Pending",
    total_price: "",
    created_by: "Admin",
    updated_by: "",
    cancellation_reason: "",
    status: "Draft",
    business_unit_id: 1,
  });
  const [isReadyToRender, setIsReadyToRender] = useState(false);
  useReservationInitializer({
    navigate,
    businessUnitId,
    setIsRecoveringReservation,
    setFormData,
    setIsReadyToRender,
  });

  const location = useLocation();

  const [forceUpdateTrigger, setForceUpdateTrigger] = useState(0);

  const [showBookerPopup, setShowBookerPopup] = useState(false);
  const [showMainGuestPopup, setShowMainGuestPopup] = useState(false);
  const [showAccompanyingPopup, setShowAccompanyingPopup] = useState(false);
  const [activeAccompanyingIndex, setActiveAccompanyingIndex] = useState(null);
  const [showChildPopup, setShowChildPopup] = useState(false);
  const [activeChildIndex, setActiveChildIndex] = useState(null);


  
  
  
  const customerIdFromURL = searchParams.get("customer_id") || "";
  

  

  // 📦 Referencias y sincronización
  const reservationIdRef = useRef(null);
  useSyncReservationId(location, reservationIdRef);
  const isSavingRef = useRef(false);
  window.reservationIdRef = reservationIdRef;
  window.isSavingRef = isSavingRef;
  const isResettingRef = useRef(false);
  const pendingMainGuestId = useRef(null);
  

  // 📋 Estado de formulario y datos de cliente
  const [customerData, setCustomerData] = useState(null);
  const [bookerData, setBookerData] = useState(null);
  const [guestCustomers, setGuestCustomers] = useState([]);
  const [guestChildren, setGuestChildren] = useState([]);
  

  const [activeReservationId, setActiveReservationId] = useState(reservationIdRef.current);

  useLoadReservationData({
    reservationIdRef,
    location,
    setCustomerData,
    setGuestChildren,
    setGuestCustomers,
    setFormData,
    activeReservationId,
  });

  const [requiredCustomerFieldsReservation, setRequiredCustomerFieldsReservation] = useState({});
  const [settings, setSettings] = useState({});
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  
  // 👥 Guests - Clientes relacionados
  
  const [editingAccompanyingIndex, setEditingAccompanyingIndex] = useState(null);
  const [editingChildIndex, setEditingChildIndex] = useState(null); 
  
  const [groupReservations, setGroupReservations] = useState([]);
  const [groupData, setGroupData] = useState(null);

  const { handleSubmitCustomer, customerFormData, setCustomerFormData, handleCustomerChange, handleCustomerCountryChange, handleCancel } = useCustomerForm({
    showNewCustomerForm,
    editingChildIndex,
    editingAccompanyingIndex,
    bookerData,
    setBookerData,
    customerData,
    setCustomerData,
    formData,
    setGroupReservations,
    requiredCustomerFieldsReservation,
    settings,
    navigate,
    reservationIdRef,
    pendingMainGuestId,
    businessUnitId,
    setEditingChildIndex,
    setEditingAccompanyingIndex,
    setShowNewCustomerForm,
    setGuestCustomers,
    setGuestChildren,
    guestChildren
  });

  useValidateReservationCustomers({
  customerData,
  bookerData,
  guestCustomers,
  guestChildren,
  requiredCustomerFieldsReservation,
  settings,
  setCustomerFormData,
  setShowNewCustomerForm,
  setEditingAccompanyingIndex,
  setEditingChildIndex,
  setCustomerData,
  setBookerData,
  setGuestCustomers,
  setGuestChildren,
  pendingMainGuestId
  });


  // 🎨 UI - Estados de edición y visibilidad
  
  const [isEditingAdults, setIsEditingAdults] = useState(false);
  const [isEditingChildren, setIsEditingChildren] = useState(false);
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [isEditingRoomCategory, setIsEditingRoomCategory] = useState(false); // 14.06.2025 - 19:40
  const [isEditingRoom, setIsEditingRoom] = useState(false);
  const [isEditingRate, setIsEditingRate] = useState(false);

  // 📅 Calendario - Rango de fechas temporal
  const [tempRange, setTempRange] = useState({
    startDate: null,
    endDate: null,
  });

  
  

  // 🏨 Grupo de reservas
  
  const [selectedReservationId, setSelectedReservationId] = useState(reservationIdRef.current);
  
  const [roomToAdd, setRoomToAdd] = useState(1);
  const [roomCategoriesByReservation, setRoomCategoriesByReservation] = useState({});

  // 🔁 Control de render y validación general
 
  const [hasAnyDates, setHasAnyDates] = useState(false);
  const [hasAnyRoomRate, setHasAnyRoomRate] = useState(false);

  const [copyFromOptions, setCopyFromOptions] = useState([]);
  const [copyToOptions, setCopyToOptions] = useState([]);

  const [dbDatesMap, setDbDatesMap] = useState({});
  const [adultsMap, setAdultsMap] = useState({});
  const [childrenMap, setChildrenMap] = useState({});
  const [roomCategoryMap, setRoomCategoryMap] = useState({});
  const [roomCategoryDetailsMap, setRoomCategoryDetailsMap] = useState({});
  const [roomIdMap, setRoomIdMap] = useState({});
  const [roomRateDetailsMap, setRoomRateDetailsMap] = useState({});
  const [groupRateIdMap, setGroupRateIdMap] = useState({});
  
  

  const [isReservationReady, setIsReservationReady] = useState(false);
    
  //comenzamos aquí los cambios
  useReservationGroup({
    reservationIdRef,
    setGroupReservations,
    setSelectedReservationId,
    setFormData,
    setBookerData,
    setGroupData,
    setHasAnyDates,
    setCopyFromOptions,
    setCopyToOptions,
    location,
    reloadDatesFromDB,
    reloadAdultsFromDB,
    reloadChildrenFromDB,
    reloadRoomCategoryIdFromDB,
    reloadRoomCategoryDetailsFromDB,
    reloadRoomIdFromDB,
    reloadRoomRateIdFromDB,
    setDbDatesMap,
    setAdultsMap,
    setChildrenMap,
    setRoomCategoryMap,
    setRoomCategoryDetailsMap,
    setRoomIdMap,
    setRoomRateDetailsMap,
    searchParams,
    formData,
    
    
  });  

  // ✅ Keep formData in sync with the active draft (avoid cross-room data bleed)
  useEffect(() => {
    if (!selectedReservationId || selectedReservationId === "summary") return;

    console.log("[RES][TabSync] Switching active reservation", {
      selectedReservationId
    });

    // 1) Sync ref (autosave source of truth)
    reservationIdRef.current = selectedReservationId;

    // 2) Reset UI immediately (only volatile UI fields, DB is source of truth)
    setFormData((prev) => ({
      ...prev,
      id: selectedReservationId,

      // ❌ NO resetear campos persistentes del draft
      // room_category_id
      // room_id
      // rate_id
      // group_rate_id
      // check_in_date / check_out_date
      // adults / children / babies
      // prices

      // ✅ SOLO limpiar UI derivada / volátil
      extras: [],
      extras_details: [],
      total_extras: 0
    }));


    // 3) Reload the draft from DB and set formData
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${selectedReservationId}`
        );

        console.log("[RES][TabSync][DB][OK] Draft loaded", {
          id: selectedReservationId
        });

        setFormData((prev) => ({
          ...prev,
          id: data.id,

          check_in_date: data.check_in_date || "",
          check_in_time: data.check_in_time || "15:00",
          check_out_date: data.check_out_date || "",
          check_out_time: data.check_out_time || "12:00",

          arrival_date: data.arrival_date || "",
          arrival_time: data.arrival_time || "",
          departure_date: data.departure_date || "",
          departure_time: data.departure_time || "",

          room_category_id: data.room_category_id || "",
          room_id: data.room_id || "",
          rate_id: data.rate_id || "",
          group_rate_id: data.group_rate_id || null,

          adults: data.adults ?? "",
          children: data.children ?? "",
          children_ages: Array.isArray(data.children_ages) ? data.children_ages.join(",") : (data.children_ages || ""),
          babies: data.babies ?? 0,

          special_requests: data.special_requests || "",
          payment_status: data.payment_status || "Pending",

          total_price: data.total_price ?? "",
          night_price: data.night_price ?? "",

          extras: data.extras || [],
          extras_details: data.extras_details || [],
          total_extras: data.total_extras || 0
        }));
      } catch (err) {
        console.error("[RES][TabSync][DB][ERR] Failed loading draft", {
          id: selectedReservationId,
          err
        });
      }
    })();
  }, [selectedReservationId]);

  // 🔄 Copy Info Panel
  const [copyFrom, setCopyFrom] = useState("");
  const [copyTo, setCopyTo] = useState("");
  const [showCopyPanel, setShowCopyPanel] = useState(false);

  const { handleCopyReservationInfo } = useCopyReservationInfo({
  setAdultsMap,
  setChildrenMap,
  setDbDatesMap,
  setRoomCategoryMap,
  setRoomRateDetailsMap,
  setHasAnyDates,
  setCopyFromOptions,
  setCopyToOptions,
  setShowCopyPanel,
  setCopyFrom,
  setCopyTo,
  formData,
  copyFrom,
  copyTo,
  copyToOptions,
});

const {
  handleAddNewAccompanyingGuest,
  handleAddNewChild,
  handleSelectChild,
  handleRemoveChild,
  handleSubmitAccompanyingGuest,
  handleSubmitChild,
  handleRemoveAccompanyingGuest,
  handleSelectAccompanyingGuest

} = useGuestManagementHandlers({
    guestCustomers,
    setGuestCustomers,
    guestChildren,
    setGuestChildren,
    setCustomerFormData,
    setShowNewCustomerForm,
    customerIdFromURL,
    showNewCustomerForm,
    editingChildIndex,
    customerFormData,
    setEditingChildIndex,
    businessUnitId,
    navigate,
    searchParams
});

  /*const getCurrentGuestType = () =>*/
  getGuestContext({
    showNewCustomerForm,
    editingChildIndex,
    editingAccompanyingIndex,
  });
  
  // 🐞 Debug: log de cambio de guestCustomers
  useEffect(() => {
    console.log("🔁 guestCustomers actualizado:", guestCustomers);
  }, [guestCustomers]);

  // 🖼️ Efectos visuales de scroll
  useEffect(() => {
    document.body.classList.add("check-in-scroll");

    return () => {
        document.body.classList.remove("check-in-scroll");
    };
  }, []);

  useEffect(() => {
  if (forceUpdateTrigger === 0) return; // Ignorar render inicial

  // Reseteamos campos dependientes en formData
  debugSetFormData("RoomRate:refreshDraft")(prev => ({
    ...prev,
    room_category_id: "",
    room_id: "",
    rate_id: "",
    group_rate_id: null,
    total_price: null,
    night_price: null,
  }));

  // Aquí también puedes resetear mapas relacionados, si aplica
  setRoomCategoryMap(prev => ({ ...prev, [reservationIdRef.current]: null }));
  setRoomIdMap(prev => ({ ...prev, [reservationIdRef.current]: null }));
  setRoomRateDetailsMap(prev => ({ ...prev, [reservationIdRef.current]: null }));

}, [forceUpdateTrigger]);


  

  // 🔄 Guardado automático para reservas en estado Draft
  const handleAutoSave = async (fieldName, value) => {
    console.log("🧨 Entró en handleAutoSave con campo:", fieldName, "valor:", value);
    const reservationId = reservationIdRef.current ?? null;

    if (!reservationId) {
      console.warn("⛔ AutoSave abortado: reservationId NULL", {
        fieldName,
        value,
        source: "handleAutoSave"
      });
      return;
    }

    console.log("🧨 Entró en handleAutoSave con campo:", fieldName);


    if (!reservationId || formData.status !== "Draft" || window.__suppressAutoSave) {
      console.log("⏩ AutoSave ignorado: no hay ID, no es Draft o está en medio de un reset.");
      return;
    }


    // 🛡️ Evitar guardar campos numéricos vacíos (causa error en postgres)
    if ((fieldName === "room_id" || fieldName === "room_category_id") && value === "") {
      console.log(`⚠️ No se guarda ${fieldName} porque está vacío`);
      return;
    }
    

    try {
      const payload = {
        updated_by: "AutoSave"
      };

      if (fieldName === "room_category_id") {
      payload.room_category_id = Number(value);
      payload.room_id = null;
      payload.rate_id = null;
      payload.total_price = null;
      payload.night_price = null;
    } else if (fieldName === "adults" || fieldName === "children") {
  // Parseo ambos para la lógica de validación/reset, pero SOLO añado al payload el campo modificado
  const adultsVal = fieldName === "adults" ? value : formData.adults;
  const childrenVal = fieldName === "children" ? value : formData.children;

  const parsedAdults = parseInt(adultsVal, 10);
  const parsedChildren = parseInt(childrenVal, 10);

  // SOLO añade al payload el campo modificado
  if (fieldName === "adults") {
    payload.adults = isNaN(parsedAdults) || parsedAdults < 1 ? 1 : parsedAdults;
  }
  if (fieldName === "children") {
    payload.children = isNaN(parsedChildren) || parsedChildren < 0 ? 0 : parsedChildren;
  }

  // Validación y reset de categoría, igual que antes
  const oldCat = roomCategoryMap?.[formData.room_category_id];

  const isInvalidNow =
    oldCat &&
    (oldCat.max_adults < parsedAdults || oldCat.max_children < parsedChildren);

  const currentRoomCategoryId = Number(formData.room_category_id);
  
  } else {
    payload[fieldName] = value;
  }




      console.log("💾 Guardando campo en backend (Draft):", payload);

      console.log("🚀 PATCH enviado a /api/reservations/:id/draft", {
        id: reservationId,
        fieldName,
        value,
        payload,            // Aquí ves el objeto completo que se va a enviar
        from: "handleAutoSave"
      });


      axios.patch(
        `${process.env.REACT_APP_API_URL}/api/reservation-drafts/${reservationId}`,
        payload
      );


      console.log("✅ Guardado automático exitoso.");
      if (["check_in_date", "check_out_date"].includes(fieldName)) {
        await reloadDatesFromDB(reservationId, setDbDatesMap);
      }

      if (fieldName === "adults") {
        await reloadAdultsFromDB(reservationId, setAdultsMap);
        await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
        await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);

      }

      if (fieldName === "children") {
        await reloadChildrenFromDB(reservationId, setChildrenMap);
        await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
        await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);

      }



      if (fieldName === "room_category_id") {
        await reloadRoomCategoryIdFromDB(reservationId, setRoomCategoryMap);
        await reloadRoomCategoryDetailsFromDB(reservationId, setRoomCategoryDetailsMap);
      }

      if (fieldName === "room_id") {
        await reloadRoomIdFromDB(reservationId, setRoomIdMap);
      }

      if (fieldName === "rate_id") {
        await reloadRoomRateIdFromDB(reservationId, setRoomRateDetailsMap);
      }

      // 🔁 Forzar refresh de Copy Info si se ha guardado rate_id
      if (fieldName === "rate_id" && formData.reservation_group_id) {
        try {
          const res = await axios.get(`/api/reservation-groups/${formData.reservation_group_id}/copy-info-options`);
          setHasAnyRoomRate(res.data.has_any_room_rate);
          setCopyFromOptions(res.data.copy_from_options);
          setCopyToOptions(res.data.copy_to_options);
          console.log("✅ Copy Info actualizado tras guardar rate_id manualmente");
        } catch (err) {
          console.error("❌ Error al forzar refresh de Copy Info:", err);
        }
      }







      } catch (error) {
      console.error("❌ Error al guardar automáticamente el campo:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

    }
  };

  // --- AÑADIR FUERA DEL useEffect, al inicio del componente ---
  const reloadGroupReservations = async () => {
    if (!formData.reservation_group_id) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-drafts/reservations/group/${formData.reservation_group_id}`
      );
      setGroupReservations(res.data);

      console.log("📦 [reloadGroupReservations] Group reservations updated:", res.data);
    } catch (error) {
      console.error("❌ Error recargando group reservations:", error);
    }
  };
  // --- FIN DE NUEVO BLOQUE ---


  // 📊 Cargar datos de grupo con detalles para la pestaña "Summary"
  useEffect(() => {
    if (selectedReservationId !== "summary") return;
    if (!formData.reservation_group_id) return;

    const fetchSummaryData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/reservation-drafts/reservations/group/${formData.reservation_group_id}`
        );
        setGroupReservations(res.data);

        console.log("📦 [setGroupReservations] (summary load) Group reservations updated from with-details:", res.data);
      } catch (error) {
        console.error("❌ Error cargando resumen detallado:", error);
      }
    };

    fetchSummaryData();
  }, [selectedReservationId, formData.reservation_group_id]);
    
    //Obtener y aplicar defaults (check-in/out)
    useEffect(() => {
      const fetchDefaultTimes = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: { business_unit_id: businessUnitId }
          });
    
          const settingsFromBackend = response.data;
          setSettings(settingsFromBackend); // ← única línea añadida
          setRequiredCustomerFieldsReservation(settingsFromBackend.required_customer_fields_reservation || {});
    
          console.log("🕒 Settings por defecto recibidos:", settingsFromBackend);
    
          // Solo aplicar si están vacíos en el formData actual
          setFormData((prev) => {
            const updated = { ...prev };

            console.log("🐞 check_in_date antes de setDefault:", prev.check_in_date);
            if (!prev.check_in_time) {
              updated.check_in_time = settingsFromBackend.checkin_time || "";
              handleAutoSave("check_in_time", settingsFromBackend.checkin_time || "");
              console.log("💾 AutoSave → check_in_time", settingsFromBackend.checkin_time);
            }
    
            if (!prev.check_out_time) {
              updated.check_out_time = settingsFromBackend.checkout_time || "";
              handleAutoSave("check_out_time", settingsFromBackend.checkout_time || "");
              console.log("💾 AutoSave → check_out_time", settingsFromBackend.checkout_time);
            }

            console.log("✅ check_in_date después de setFormData:", updated.check_in_date);
            return updated;
          });
          
    
        } catch (error) {
          console.warn("⚠️ No se pudo cargar check-in/check-out por defecto:", error);
        }
      };
    
      fetchDefaultTimes();
    }, [businessUnitId]);
    
  

  //Recalcula Room Nights
  useEffect(() => {
  if (!formData.check_in_date || !formData.check_out_date) {
    if (formData.room_nights !== "") {
      setFormData(prev => ({ ...prev, room_nights: "" }));
    }
    return;
  }

  const checkIn = new Date(formData.check_in_date);
  const checkOut = new Date(formData.check_out_date);
  const diffDays = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

  if (diffDays < 1 || diffDays === formData.room_nights) return;

  debugSetFormData("RoomRate:refreshDraft")(prev => ({
    ...prev,
    room_nights: diffDays
  }));

  const pathMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
  const reservationId = pathMatch ? pathMatch[1] : null;

  if (reservationId) {
    handleAutoSave("room_nights", diffDays);
  }
}, [formData.check_in_date, formData.check_out_date]);

const rateIds = useMemo(() => groupReservations.map(r => r.rate_id), [groupReservations]);

useEffect(() => {
  if (!formData.reservation_group_id) return;

  const fetchCopyInfo = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/reservation-groups/${formData.reservation_group_id}/copy-info-options`
      );
      setHasAnyRoomRate(res.data.has_any_room_rate);
      setCopyFromOptions(res.data.copy_from_options);
      setCopyToOptions(res.data.copy_to_options);
      console.log("✅ [AUTO] Copy Info actualizado tras esperar base de datos:", res.data);
    } catch (error) {
      console.error("❌ Error refrescando Copy Info:", error);
    }
  };

  fetchCopyInfo();
}, [formData.reservation_group_id, rateIds]);




  // States for room categories, rooms, and rates
const [roomCategories, setRoomCategories] = useState([]);
const [rooms, setRooms] = useState([]);
const [roomRates, setRoomRates] = useState([]);


// Nuevos estados para las opciones de adultos y niños
const [availableAdults, setAvailableAdults] = useState([]);
const [availableChildren, setAvailableChildren] = useState([]);
const [nightlyPrice, setNightlyPrice] = useState(null);


const {
    minCheckInDate,
    handleChange,
    handleResetDates,
    handleResetCapacityOnly,
    handleResetChildrenCapacityOnly,
    handleResetRoomCategoryOnly,
    handleDeleteReservation
  } = useReservationHandlers({
    formData,
    setFormData,
    setAvailableAdults,
    setAvailableChildren,
    setRoomCategories,
    setGuestCustomers,
    setGuestChildren,
    setNightlyPrice,
    isResettingRef,
    businessUnitId,
    selectedReservationId,
    setRoomCategoryMap,
    setRoomIdMap,
    refetchReservationAfterReset,
    reloadDatesFromDB,
    setDbDatesMap,
    reloadRoomCategoryIdFromDB,
    setRoomCategoryDetailsMap,
    reloadRoomCategoryDetailsFromDB,
    reloadAdultsFromDB,
    setAdultsMap,
    reloadChildrenFromDB,
    setChildrenMap,
    setCustomerData,
    reloadRoomIdFromDB,
    reloadRoomRateIdFromDB,
    setRoomRateDetailsMap,
    reservationIdRef,
    groupReservations,
    });




/*const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "32px",
    fontSize: "0.85rem",
    backgroundColor: "#222",
    borderColor: state.isFocused ? "#666" : "#444",
    boxShadow: "none",
    color: "#fff",
    borderRadius: "4px",
    padding: "0 4px"
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1c1c1c",
    fontSize: "0.85rem",
    zIndex: 10001
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#333" : "#1c1c1c",
    color: "#fff",
    cursor: "pointer"
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#fff",
    fontSize: "0.85rem"
  }),
  input: (styles) => ({
    ...styles,
    color: "#fff"
  }),
  placeholder: (styles) => ({
    ...styles,
    color: "#bbb",
    fontSize: "0.85rem"
  })
};*/





const hasAnyIncompleteReservation = useMemo(() => {
  return groupReservations.some(res =>
    // Excluye la reserva seleccionada, si quieres permitir solo una “a medias”
    res.id !== selectedReservationId &&
    (res.check_in_date || res.check_out_date) &&
    !res.rate_id
  );
}, [groupReservations, selectedReservationId]);



const reservationInProgressId = useMemo(() => {
  // Devuelve el id de la reserva "a medias" o null si no hay ninguna
  const found = groupReservations.find(res =>
    (res.check_in_date || res.check_out_date) &&
    !res.rate_id
  );
  return found ? found.id : null;
}, [groupReservations]);





 

  useEffect(() => {
    if (businessUnitId) {
      setFormData((prev) => ({ ...prev, business_unit_id: Number(businessUnitId) }));
    }
  }, [businessUnitId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (settings.required_customer_roles_reservation?.includes("main_guest")) {
      const reservasSinMainGuest = [];

      for (const reservation of groupReservations) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservation.id}`
          );
          const guests = res.data || [];
          const tieneMainGuest = guests.some(g => g.is_main_guest);
          if (!tieneMainGuest) reservasSinMainGuest.push(reservation);
        } catch (err) {
          console.warn(`⚠️ Error al verificar main guest en draft ${reservation.id}:`, err);
          reservasSinMainGuest.push(reservation);
        }
      }

      if (reservasSinMainGuest.length > 0) {
        alert(`❌ No se puede guardar el grupo. Las siguientes reservas no tienen main guest asignado:\n\n${reservasSinMainGuest.map(r => `Room ${r.group_index + 1}`).join("\n")}`);
        return;
      }
    }


  isSavingRef.current = true;
  let newStatus = null;
   

  // Validaciones de campos requeridos
  const requiredFields = {
    "Check-in Date": formData.check_in_date,
    "Check-out Date": formData.check_out_date,
    "Room Category": formData.room_category_id,
    "Rate or Group Rate": formData.rate_id || formData.group_rate_id,
    "Number of Adults": formData.adults,
  };

  const missing = Object.entries(requiredFields).filter(([_, value]) => !value || value === "");

  if (missing.length > 0) {
    const message = missing.map(([label]) => `• ${label}`).join("\n");
    alert(`❌ Required fields are missing:\n\n${message}`);
    return;
  }


  const requiredRoles = settings?.required_customer_roles_reservation || [];

  const missingRoles = [];

  if (requiredRoles.includes("booker") && !bookerData) {
    missingRoles.push("booker");
  }

  if (requiredRoles.includes("main_guest") && !customerData) {
    missingRoles.push("main guest");
  }

  if (
    requiredRoles.includes("accompanying") &&
    Number(formData.adults) > 1 &&
    guestCustomers.filter(Boolean).length === 0
  ) {
    missingRoles.push("accompanying guest");
  }

  if (
    requiredRoles.includes("child") &&
    Number(formData.children) > 0 &&
    guestChildren.filter(Boolean).length === 0
  ) {
    missingRoles.push("child");
  }

  if (missingRoles.length > 0) {
    alert(`❌ Settings require the following roles:\n\n• ${missingRoles.join("\n• ")}`);
    return;
  }


  const normalizedChildren = formData.children === "" ? 0 : parseInt(formData.children, 10);
  const normalizedAdults = formData.adults === "" ? 1 : parseInt(formData.adults, 10);

  console.log("📦 Valor de formData.status antes de construir el payload:", formData.status);

  // ✅ Eliminamos explícitamente 'status' del formData
const {
  status: _omitStatus, // lo descartamos
  ...safeFormData
} = formData;

  const reservationPayload = {
    ...safeFormData,
    customer_id: customerIdFromURL,
    room_id: formData.room_id === "" ? null : formData.room_id,
    rate_id: formData.rate_id === "" ? null : Number(formData.rate_id),
    children_ages: formData.children_ages ? formData.children_ages.split(",").map(Number) : [],
    room_category_id: Number(formData.room_category_id),
    adults: normalizedAdults,
    children: normalizedChildren,
    guests: [
      {
        customer_id: customerIdFromURL,
        is_main_guest: true,
        first_name: customerData?.first_name || "Desconocido",
        last_name: customerData?.last_name || "Desconocido",
        country: customerData?.country || "Unknown",
        document_number: customerData?.document_number || null,
        document_type: customerData?.document_type || null
      }
    ]
  };

  

  

  // 👇 AÑADE AQUÍ
  console.log("🚀 Payload que voy a enviar al backend:", reservationPayload);

  // Eliminar campos de fecha vacíos
  const dateFields = ["arrival_date", "arrival_time", "departure_date", "departure_time"];
  dateFields.forEach((field) => {
    if (reservationPayload[field] === "") {
      delete reservationPayload[field];
    }
  });

  console.log("🚀 FINAL PAYLOAD QUE SE ENVÍA AL BACKEND:", reservationPayload);

  try {
    // ✅ Reescribimos status ANTES de enviar
    try {
      console.log("🚧 DEBUG → formData.rate_id:", formData.rate_id);

      if (!formData.rate_id && !formData.group_rate_id) {
        alert("❌ No rate selected (rate_id) nor group rate (group_rate_id).");
        return;
      }

      if (formData.group_rate_id) {
        // Si hay tarifa de grupo, usar settings
        reservationPayload.status = settings.group_reservations_default_status;
        console.log(`🎯 Status set from settings for group reservation: ${reservationPayload.status}`);
      } else if (formData.rate_id) {
        // Solo ejecuta esto si hay rate_id
        const rateRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/${Number(formData.rate_id)}`);
        const rateClientId = rateRes.data.client_id;

        console.log("📄 rateRes:", rateRes.data);

        const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: businessUnitId }
        });

        const list = (settingsRes.data.clients_require_requested_flow || "")
          .split(",")
          .map((x) => x.trim());

        const clientStr = rateClientId === null ? "null" : String(rateClientId);
        reservationPayload.status = list.includes(clientStr) ? "Requested" : "Confirmed";

        console.log(`🎯 Status fijado por frontend: ${reservationPayload.status}`);
      }

    } catch (err) {
      console.error("❌ Error al fijar status automáticamente:", err);
      console.log("🧨 Error .response?.data:", err.response?.data);
      console.log("🧨 Error .message:", err.message);
      alert("No se pudo determinar el estado final de la reserva.");
      return;
    }
  
    /*let response;*/

    console.log("🟡 settings.group_reservations_default_status:", settings.group_reservations_default_status);
    console.log("🟡 settings:", settings);

  
    for (const res of groupReservations) {
      const reservationId = res.id;

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}`);
        const data = response.data;
        console.log("🔍 [PRUEBA CLAVE] DATA DEL BACKEND:", JSON.stringify(data, null, 2));

        const requiredFields = {
          "Check-in Date": data.check_in_date,
          "Check-out Date": data.check_out_date,
          "Room Category": data.room_category_id,
          "Rate or Group Rate": data.rate_id || data.group_rate_id,
          "Number of Adults": data.adults
        };

        const missing = Object.entries(requiredFields).filter(([_, value]) => !value || value === "");

        if (missing.length > 0) {
          const message = missing.map(([label]) => `• ${label}`).join("\n");
          alert(`❌ Required fields are missing in room ${res.group_index + 1}:\n\n${message}`);
          return;
        }

        let newStatus;

        if (data.group_rate_id) {
          // Para reservas de grupo, el status sale DIRECTAMENTE de settings
          newStatus = settings.group_reservations_default_status;
          console.log(`🎯 Status from settings for group reservation: ${newStatus}`);
        } else if (data.rate_id) {
          // Para reservas individuales, SÍ se hace GET y lógica especial
          const rateRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/${data.rate_id}`);
          const clientId = rateRes.data.client_id;

          const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: { business_unit_id: data.business_unit_id }
          });

          const list = (settingsRes.data.clients_require_requested_flow || "")
            .split(",")
            .map(x => x.trim());

          const clientStr = clientId === null ? "null" : String(clientId);
          newStatus = list.includes(clientStr) ? "Requested" : "Confirmed";
        } else {
          alert(`❌ No rate or group rate in room ${res.group_index + 1}.`);
          return;
        }

        await axios.put(`${process.env.REACT_APP_API_URL}/api/reservations/${reservationId}`, {
          status: newStatus,
          check_in_time: data.check_in_time,
          check_out_time: data.check_out_time,
          room_nights: data.room_nights, // ← 🔥 AÑADIDO
          updated_by: "GroupConfirmation"
        });

        console.log(`✅ Reserva #${reservationId} actualizada a ${newStatus}`);
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || "Unknown error";
        console.error(`❌ Error confirming reservation #${res.id}:`, errorMsg);
        alert(`❌ Error while confirming room ${res.group_index + 1}:\n\n${errorMsg}`);
        return;
      }
    }

    
    navigate(`/reservations?business_unit_id=${businessUnitId}&saved_status=${newStatus}`);
     
      
  } catch (error) {
    console.error("❌ Error saving reservation:", error);
    const serverMessage = error.response?.data?.error;
    if (serverMessage) {
      alert(`❌ Error while saving reservation:\n\n${serverMessage}`);
    } else {
      alert("An unexpected error occurred while saving the reservation.");
    }
  }
  
  
  

  // Limpiar formulario
  setFormData({
    check_in_date: "",
    check_in_time: "",
    check_out_date: "",
    check_out_time: "",
    arrival_date: "",
    arrival_time: "",
    departure_date: "",
    departure_time: "",
    room_category_id: "",
    room_id: "",
    rate_id: "",
    group_rate_id: null,
    room_nights: "",
    adults: "",
    children: "",
    children_ages: "",
    babies: 0,
    special_requests: "",
    payment_status: "Pending",
    total_price: "",
    created_by: "Admin",
    updated_by: "",
    cancellation_reason: "",
    status: "Draft",
    business_unit_id: 1,
  });
};


const currentURL = new URL(window.location.href);
/*const guestType = currentURL.searchParams.get("guest_type");
const guestIndex = currentURL.searchParams.get("guest_index");*/

let requiredFields = [];

const role = getGuestContext();

if (requiredCustomerFieldsReservation && role) {
  requiredFields = requiredCustomerFieldsReservation[role] || [];
}

if (!isReadyToRender || isRecoveringReservation) {
  return (
    <div className="text-center text-light p-5">
      <h3 className="text-warning"></h3>
    </div>
  );
}

console.log("🧪 Mostrar botón Copy Info?");
console.log("→ groupReservations.length:", groupReservations.length);
console.log("→ hasAnyDates:", hasAnyDates);

if (!isReadyToRender) {
  return null; // ⛔ Nada hasta que esté todo cargado
}

return (
  <div style={{ width: "100vw", padding: "2rem 2rem 0 2rem", overflowX: "hidden" }}>
    <div className="d-flex justify-content-between px-0" style={{ width: "100%", gap: "0" }}>
      <div style={{ flex: 1 }}>
        {formData.reservation_group_id && formData.group_locator && (
          <GroupInfoCard
            formData={formData}
            permissions={permissions}
            roomToAdd={roomToAdd}
            setRoomToAdd={setRoomToAdd}
            groupReservations={groupReservations}
            setGroupReservations={setGroupReservations}
            setFormData={setFormData}
            setHasAnyDates={setHasAnyDates}
            setCopyFromOptions={setCopyFromOptions}
            setCopyToOptions={setCopyToOptions}
            businessUnitId={businessUnitId}
          />
        )}

        <ReservationTabsPanel
          groupReservations={groupReservations}
          selectedReservationId={selectedReservationId}
          setSelectedReservationId={setSelectedReservationId}
          setActiveReservationId={setActiveReservationId}
          setCustomerData={setCustomerData}
          navigate={navigate}
          businessUnitId={businessUnitId}
          formData={formData}
          setFormData={setFormData}
          reservationIdRef={reservationIdRef}
          reloadDatesFromDB={reloadDatesFromDB}
          setDbDatesMap={setDbDatesMap}
          reloadAdultsFromDB={reloadAdultsFromDB}
          setAdultsMap={setAdultsMap}
          reloadChildrenFromDB={reloadChildrenFromDB}
          setChildrenMap={setChildrenMap}
          reloadRoomCategoryIdFromDB={reloadRoomCategoryIdFromDB}
          setRoomCategoryMap={setRoomCategoryMap}
          reloadRoomCategoryDetailsFromDB={reloadRoomCategoryDetailsFromDB}
          setRoomCategoryDetailsMap={setRoomCategoryDetailsMap}
          reloadRoomIdFromDB={reloadRoomIdFromDB}
          setRoomIdMap={setRoomIdMap}
          reloadRoomRateIdFromDB={reloadRoomRateIdFromDB}
          setRoomRateDetailsMap={setRoomRateDetailsMap}
          reloadGroupRateIdFromDB={reloadGroupRateIdFromDB}
          setGroupRateIdMap={setGroupRateIdMap}
          permissions={permissions}
          hasAnyDates={hasAnyDates}
          hasAnyRoomRate={hasAnyRoomRate}
          showCopyPanel={showCopyPanel}
          setShowCopyPanel={setShowCopyPanel}
          copyFrom={copyFrom}
          setCopyFrom={setCopyFrom}
          copyTo={copyTo}
          setCopyTo={setCopyTo}
          copyFromOptions={copyFromOptions}
          copyToOptions={copyToOptions}
          handleCopyReservationInfo={handleCopyReservationInfo}
          setGroupReservations={setGroupReservations}
        />

        {selectedReservationId !== "summary" && (
            <ReservationForm
              key={selectedReservationId}
              formData={formData}
              setFormData={setFormData}
              handleChange={handleChange}
              handleResetDates={handleResetDates}
              minCheckInDate={minCheckInDate}
              isResettingRef={isResettingRef}
              dbDatesMap={dbDatesMap}
              setDbDatesMap={setDbDatesMap}
              adultsMap={adultsMap}
              setAdultsMap={setAdultsMap}
              childrenMap={childrenMap}
              setChildrenMap={setChildrenMap}
              availableAdults={availableAdults}
              setAvailableAdults={setAvailableAdults}
              availableChildren={availableChildren}
              setAvailableChildren={setAvailableChildren}
              visibleRoomCategories={roomCategoriesByReservation?.[selectedReservationId] || []}
              isEditingAdults={isEditingAdults}
              setIsEditingAdults={setIsEditingAdults}
              isEditingChildren={isEditingChildren}
              setIsEditingChildren={setIsEditingChildren}
              isEditingDates={isEditingDates}
              setIsEditingDates={setIsEditingDates}
              isEditingRoomCategory={isEditingRoomCategory}
              setIsEditingRoomCategory={setIsEditingRoomCategory}
              isEditingRoom={isEditingRoom}
              setIsEditingRoom={setIsEditingRoom}
              isEditingRate={isEditingRate}
              setIsEditingRate={setIsEditingRate}
              handleAutoSave={handleAutoSave}
              roomCategoryDetailsMap={roomCategoryDetailsMap}
              setRoomCategoryMap={setRoomCategoryMap}
              roomIdMap={roomIdMap}
              setRoomIdMap={setRoomIdMap}
              roomRates={roomRates}
              setRoomRates={setRoomRates}
              roomRateDetailsMap={roomRateDetailsMap}
              setRoomRateDetailsMap={setRoomRateDetailsMap}
              rooms={rooms}
              selectedReservationId={selectedReservationId}
              reservationIdRef={reservationIdRef}
              showCopyPanel={showCopyPanel}
              groupReservations={groupReservations}
              handleSubmit={handleSubmit}
              handleDeleteReservation={handleDeleteReservation}
              handleResetCapacityOnly={handleResetCapacityOnly}
              handleResetChildrenCapacityOnly={handleResetChildrenCapacityOnly}
              handleResetRoomCategoryOnly={handleResetRoomCategoryOnly}
              tempRange={tempRange}
              setTempRange={setTempRange}
              formatDate={formatDate}
              reloadDatesFromDB={reloadDatesFromDB}
              hasAnyIncompleteReservation={hasAnyIncompleteReservation}
              isLockedByOther={
                reservationInProgressId !== null &&
                reservationInProgressId !== selectedReservationId
              }
            />

        )}
      </div>

      <GuestsColumn
        formData={formData}
        showNewCustomerForm={showNewCustomerForm}
        bookerData={bookerData}
        isEditingAnyRole={isEditingAnyRole}
        customerFormData={customerFormData}
        handleCustomerChange={handleCustomerChange}
        handleCustomerCountryChange={handleCustomerCountryChange}
        requiredFields={requiredFields}
        groupReservations={groupReservations}
        editingAccompanyingIndex={editingAccompanyingIndex}
        editingChildIndex={editingChildIndex}
        requiredCustomerFieldsReservation={requiredCustomerFieldsReservation}
        handleSubmitChild={handleSubmitChild}
        handleSubmitCustomer={handleSubmitCustomer}
        handleSubmitAccompanyingGuest={handleSubmitAccompanyingGuest}
        handleCancel={handleCancel}
        reservationIdRef={reservationIdRef}
        customerData={customerData}
        setCustomerFormData={setCustomerFormData}
        setShowNewCustomerForm={setShowNewCustomerForm}
        setBookerData={setBookerData}
        setEditingAccompanyingIndex={setEditingAccompanyingIndex}
        setEditingChildIndex={setEditingChildIndex}
        guestCustomers={guestCustomers}
        setGuestCustomers={setGuestCustomers}
        handleSelectAccompanyingGuest={handleSelectAccompanyingGuest}
        handleAddNewAccompanyingGuest={handleAddNewAccompanyingGuest}
        handleRemoveAccompanyingGuest={handleRemoveAccompanyingGuest}
        handleSelectChild={handleSelectChild}
        handleAddNewChild={handleAddNewChild}
        handleRemoveChild={handleRemoveChild}
        guestChildren={guestChildren}
        setGuestChildren={setGuestChildren}
        customerIdFromURL={customerIdFromURL}
        navigate={navigate}
        searchParams={searchParams}
        businessUnitId={businessUnitId}
        setCustomerData={setCustomerData}
        selectedReservationId={selectedReservationId}
        isSummary={selectedReservationId === "summary"}
        onOpenBookerSelector={() => setShowBookerPopup(true)} 
        onOpenMainGuestSelector={() => setShowMainGuestPopup(true)}
        onOpenAccompanyingSelector={(index) => {
          setActiveAccompanyingIndex(index);
          setShowAccompanyingPopup(true);
        }}
        onOpenChildSelector={(index) => {
          setActiveChildIndex(index);
          setShowChildPopup(true);
        }}

      />
    </div>

     {/* Popup de selección de booker */}
    <GuestSelectorPopup
      show={showBookerPopup}
      onClose={() => setShowBookerPopup(false)}
      reservationId={reservationIdRef.current}
      onCustomerSelected={async (customer) => {
        setBookerData(customer);          // ⬅️ este estado pinta la tarjeta del booker
        await reloadGroupReservations();  // refresca grupo en paralelo
      }}
    />

    {/* Popup de selección de main guest */}
    <MainGuestSelectorPopup
      show={showMainGuestPopup}
      onClose={() => setShowMainGuestPopup(false)}
      reservationId={reservationIdRef.current}
      onCustomerSelected={async (customer) => {
        setCustomerData(customer);          // ⬅️ esto pinta la tarjeta del main guest
        await reloadGroupReservations();    // refresca grupo en paralelo
      }}
    />

    {/* Popup de selección de acompañante */}
    <CustomerSelectorPopupAccompanying
      show={showAccompanyingPopup}
      onClose={() => {
        setShowAccompanyingPopup(false);
        setActiveAccompanyingIndex(null);
      }}
      reservationId={reservationIdRef.current}
      guestIndex={activeAccompanyingIndex}
      onCustomerSelected={(customer, index) => {
        // insertar en el array de guestCustomers
        const updated = [...guestCustomers];
        updated[index] = customer;
        setGuestCustomers(updated);

        // refrescar reservas del grupo
        reloadGroupReservations();
      }}
    />

    {/* Popup de selección de child */}
    <CustomerSelectorPopupChild
      show={showChildPopup}
      onClose={() => {
        setShowChildPopup(false);
        setActiveChildIndex(null);
      }}
      reservationId={reservationIdRef.current}
      guestIndex={activeChildIndex}
      onCustomerSelected={(customer, index) => {
        // insertar en el array de guestChildren
        const updated = [...guestChildren];
        updated[index] = customer;
        setGuestChildren(updated);

        // refrescar reservas del grupo
        reloadGroupReservations();
      }}
    />




    
    {selectedReservationId === "summary" && (
      <div className="card bg-dark text-light p-3 mb-3"
        style={{
          width: "100%",
          maxWidth: "92.3vw",
          zoom: "100%"
        }}
      >
        <GroupSummary 
          groupReservations={groupReservations}
          reloadGroupReservations={reloadGroupReservations}
        />

      </div>
    )}

  </div>
);
  };

  

export default NewReservation;

